import { Box } from '@mui/material';
import { useFormikContext } from 'formik';
import { filter, map } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  FormikArray,
  FormikMultiSelect,
  FormikSelect,
  FormikSelectOption,
} from 'components';
import { TranslationNamespace } from 'i18n';
import { ShopPayoutTrader } from 'types';

import { Values } from '../ManageShop';

type Props = {
  currencyExchangeOptions: FormikSelectOption[];
  traders: ShopPayoutTrader[];
};

export const ShopPayoutCurrencyExchangeConfigs: React.FC<Props> = ({
  currencyExchangeOptions,
  traders,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.shops.manage',
  });
  const formik = useFormikContext<Values>();

  const traderOptions = useMemo(
    () =>
      map(traders, (trader) => ({ label: trader.user.name, value: trader.id })),
    [traders],
  );

  const getTraderOptions = useCallback(
    (index: number): FormikSelectOption[] => {
      const selectedElsewhere = new Set(
        map(
          filter(
            formik.values.payoutCurrencyExchangeConfigs,
            (_, i) => i !== index,
          ),
          'traderIds',
        ).flat(),
      );
      return map(traderOptions, (option) => ({
        ...option,
        disabled: selectedElsewhere.has(option.value),
      }));
    },
    [formik.values.payoutCurrencyExchangeConfigs, traderOptions],
  );

  const renderItemComponent = useCallback(
    ({ index }: { index: number }) => (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            sm: 'repeat(2, minmax(0, 1fr))',
          },
          gap: 2,
          flex: 1,
          minWidth: 0,
          mr: 1,
        }}
      >
        <FormikSelect
          label={t('fields.payout_currency_exchange_config')}
          name={`payoutCurrencyExchangeConfigs[${index}].payoutCurrencyExchangeId`}
          options={currencyExchangeOptions}
          fullWidth
        />
        <FormikMultiSelect
          label={t('fields.payout_currency_exchange_config_traders')}
          name={`payoutCurrencyExchangeConfigs[${index}].traderIds`}
          options={getTraderOptions(index)}
          fullWidth
        />
      </Box>
    ),
    [t, currencyExchangeOptions, getTraderOptions],
  );

  return (
    <FormikArray
      addItemLabel={t('buttons.add_payout_currency_exchange_config')}
      name="payoutCurrencyExchangeConfigs"
      removable
      ItemComponent={renderItemComponent}
      defaultValue={{
        payoutCurrencyExchangeId: '',
        traderIds: [],
      }}
    />
  );
};
